import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { contactService } from '../services/contactService';
import axios from 'axios';

// Configure axios defaults for Laravel
// Disable withCredentials globally to fix CORS issues
axios.defaults.withCredentials = false;

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [csrfToken, setCsrfToken] = useState('');
  
  // Get CSRF token on component mount
  useEffect(() => {
    const getCsrfToken = async () => {
      try {
        // Laravel provides a /sanctum/csrf-cookie endpoint to get the CSRF cookie
        await axios.get('http://localhost/karnidigitalsolutions/backend/public/sanctum/csrf-cookie');
        console.log('CSRF cookie set');
      } catch (err) {
        console.error('Failed to get CSRF token:', err);
        // Continue anyway as we'll handle API errors later
      }
    };
    
    getCsrfToken();
  }, []);

  const services = [
    'General Enquiry',
    'Web Development',
    'Mobile App Development',
    'E-commerce Solutions',
    'Digital Marketing & SEO',
    'Travel Portal Development',
    'Real Estate Solutions',
    'POS System',
    'Booking System',
    'Rental Management System',
    'Company Management Software',
    'Custom Software Development',
    'Technical Support',
    'Consultation',
    'Other'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    console.log('=== CONTACT FORM SUBMISSION (DATABASE) ===');
    console.log('Form data:', formData);

    try {
      // Submit to database via API
      const contactData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || '',
        subject: formData.service || 'Website Enquiry',
        message: formData.message
      };
      
      console.log('Submitting to database API...');
      const result = await contactService.submitContact(contactData);
      
      if (result.success) {
        console.log('✅ SUCCESS: Contact saved to database:', result.data);
        
        // Show success message
        setSuccess(true);
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
        setTimeout(() => setSuccess(false), 5000);
      } else {
        throw new Error(result.message || 'Failed to submit contact');
      }
      
    } catch (err) {
      console.error('❌ ERROR: Failed to submit contact:', err);
      setError(err.response?.data?.message || 'Error submitting your enquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('meta.contact_title')}</title>
        <meta name="description" content={t('contact.meta_description')} />
      </Helmet>

      <section className="pt-24 pb-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 
              className="text-4xl md:text-5xl font-bold text-primary mb-4"
              data-aos="fade-up"
            >
              {t('contact.title')}
            </h1>
            <p 
              className="text-xl text-gray-600 max-w-2xl mx-auto"
              data-aos="fade-up" 
              data-aos-delay="200"
            >
              {t('contact.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div data-aos="fade-right">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-primary mb-6">
                  Get Free Quote
                </h2>

                {success && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white">
                      ✓
                    </div>
                    <div>
                      <p className="text-green-800 font-bold">Thank you for your enquiry!</p>
                      <p className="text-green-700 text-sm">We'll get back to you within 24 hours.</p>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-center gap-3">
                    <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center text-white">
                      ⚠️
                    </div>
                    <div>
                      <p className="text-yellow-800 font-semibold">{error}</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors"
                      placeholder="+66 123 456 789"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Service Needed
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors bg-white"
                    >
                      <option value="">Select a service</option>
                      {services.map((service, index) => (
                        <option key={index} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Project Details *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors resize-none"
                      placeholder="Tell us about your project requirements..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-primary hover:bg-primary-700 text-white py-4 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-5 h-5" />
                    {loading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
                
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>📞 Need immediate assistance?</strong><br />
                    Call us: <a href="tel:+66968826252" className="font-bold hover:underline">+66 96 882 6252</a><br />
                    WhatsApp: <a href="https://wa.me/66968826252" target="_blank" rel="noopener noreferrer" className="font-bold hover:underline">Chat Now</a>
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div data-aos="fade-left">
              <div className="space-y-8">
                {/* Contact Details */}
                <div className="bg-white rounded-xl shadow-lg p-8">
                  <h3 className="text-2xl font-bold text-primary mb-6">{t('contact.get_in_touch')}</h3>
                  
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <MapPin className="w-6 h-6 text-gold mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">{t('contact.address')}</h4>
                        <p className="text-gray-600">{t('contact.pattaya_address')}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <Phone className="w-6 h-6 text-gold flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">{t('contact.phone_label')}</h4>
                        <a 
                          href="tel:+66968826252" 
                          className="text-primary hover:text-primary-700"
                        >
                          +66 968826252
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className="w-6 h-6 mt-1 flex-shrink-0"></div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">{t('contact.line_label')}</h4>
                        <div className="text-gray-600">{t('contact.line_id')}</div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <Mail className="w-6 h-6 text-gold flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">{t('contact.email_label')}</h4>
                        <a 
                          href="mailto:mail@karnidigitalsolutions.com" 
                          className="text-primary hover:text-primary-700"
                        >
                          mail@karnidigitalsolutions.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <Clock className="w-6 h-6 text-gold mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">{t('contact.hours')}</h4>
                        <p className="text-gray-600">{t('contact.hours_detail')}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Google Map */}
                <div className="bg-white rounded-xl shadow-lg p-8">
                  <h3 className="text-2xl font-bold text-primary mb-6">{t('contact.our_location')}</h3>
                  <div className="aspect-video rounded-lg overflow-hidden">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d62221.1365412773!2d100.8199796959312!3d12.919221372057988!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x310297343e656c75%3A0x12cf0b53746d9dff!2sKarni%20Digital%20Solutions%20%E2%80%93%20Web%20%26%20App%20Development%20Pattaya!5e0!3m2!1sen!2sth!4v1763642213122!5m2!1sen!2sth"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Karni Digital Solutions Location"
                    />
                  </div>
                </div>

                {/* WhatsApp & LINE */}
                <div className="bg-gradient-primary rounded-xl p-8 text-white">
                  <h3 className="text-2xl font-bold mb-6">{t('contact.quick_chat')}</h3>
                  <div className="space-y-4">
                    <a
                      href="https://wa.me/66968826252"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-4 bg-white bg-opacity-20 p-4 rounded-lg hover:bg-opacity-30 transition-all"
                    >
                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold">W</span>
                      </div>
                      <span>{t('contact.chat_whatsapp')}</span>
                    </a>
                    
                    <a
                      href="https://line.me/ti/p/~kdigitalsolutions"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-4 bg-white bg-opacity-20 p-4 rounded-lg hover:bg-opacity-30 transition-all"
                    >
                      <div className="w-10 h-10 bg-green-400 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold">L</span>
                      </div>
                      <span>{t('contact.chat_line_with_id')}</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
